import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { map, Observable, shareReplay, switchMap, take, tap } from 'rxjs';
import { NgTemplateOutlet } from '@angular/common';
import { GetInvitationsListForHostQueryHandler, InvitePlayersCommandHandler } from '../../application/handlers';
import { OrAvatarComponent, OrIconComponent, ShareGameDirective } from '@ui-components';
import { IonicModule } from '@ionic/angular';
import {
  FullHeightScrollableContainerDirective,
  GameDetailsModel,
  LocalRefreshService,
  MODAL_TOKEN,
  ModalProvider
} from '@core';
import { ActivatedRoute, Params } from '@angular/router';
import { GameDetailsQueryHandler } from '../../../../../games/src/lib/application/handlers';
import { GamesService } from '@games/infra';
import { InvitationCandidate, InvitationsListQueryResult } from '../../application/handlers';
import { toSignal } from '@angular/core/rxjs-interop';

type InvitePlayerViewModel = InvitationCandidate & { isSelected: boolean };

type InvitationsViewModel = {
  availableToInvite: InvitePlayerViewModel[];
  invited: InvitationCandidate[];
  hidden: InvitationCandidate[];
};

@Component({
  selector: 'lib-hosted-invitations-list',
  templateUrl: './hosted-invitations-list.component.html',
  styleUrls: ['./hosted-invitations-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    OrAvatarComponent,
    IonicModule,
    ShareGameDirective,
    NgTemplateOutlet,
    FullHeightScrollableContainerDirective,
    OrIconComponent
  ],
  providers: [
    GetInvitationsListForHostQueryHandler,
    InvitePlayersCommandHandler,
    LocalRefreshService,
    GameDetailsQueryHandler,
    GamesService
  ]
})
export class HostedInvitationsListComponent {
  private readonly invitationsQuery: GetInvitationsListForHostQueryHandler = inject(
    GetInvitationsListForHostQueryHandler
  );
  private readonly invitePlayersCommandHandler: InvitePlayersCommandHandler = inject(InvitePlayersCommandHandler);
  private readonly localRefreshService: LocalRefreshService = inject(LocalRefreshService);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly gameDetailsQueryHandler: GameDetailsQueryHandler = inject(GameDetailsQueryHandler);

  readonly selectedUsers: WritableSignal<string[]> = signal<string[]>([]);

  private readonly gameDetails$: Observable<GameDetailsModel> = this.activatedRoute.params.pipe(
    take(1),
    map((params: Params) => params['id']),
    switchMap((gameId: string) => this.gameDetailsQueryHandler.getGameDetails(gameId)),
    shareReplay(1)
  );

  readonly gameDetails: Signal<GameDetailsModel | undefined> = toSignal(this.gameDetails$);

  private readonly invitations$: Observable<InvitationsListQueryResult> = this.localRefreshService.refresh$.pipe(
    switchMap(() => this.modalProvider.showLoading$()),
    switchMap(() => this.gameDetails$),
    switchMap(() => this.invitationsQuery.getList()),
    tap(() => this.modalProvider.dismissLoading()),
    shareReplay(1)
  );

  private readonly invitationsData: Signal<InvitationsListQueryResult | undefined> = toSignal(this.invitations$);

  readonly invitations: Signal<InvitationsViewModel | undefined> = computed(() => {
    const data: InvitationsListQueryResult | undefined = this.invitationsData();
    if (!data) return undefined;

    const selected: string[] = this.selectedUsers();
    return {
      ...data,
      availableToInvite: data.availableToInvite.map((available: InvitationCandidate) => ({
        ...available,
        isSelected: selected.includes(available.userId) || available.isFavorite
      }))
    };
  });

  readonly showInviteButton: Signal<boolean> = computed(() => {
    const invitations: InvitationsViewModel | undefined = this.invitations();
    return !!invitations?.availableToInvite?.length;
  });

  readonly hasSelectedUsers: Signal<boolean> = computed(() => this.selectedUsers().length > 0);

  selectPlayer(userId: string): void {
    const current: string[] = this.selectedUsers();

    if (current.includes(userId)) {
      this.selectedUsers.set(current.filter((id: string) => id !== userId));
    } else {
      this.selectedUsers.set([...current, userId]);
    }
  }

  selectAll(event: CustomEvent): void {
    const eventValue: boolean = event.detail.checked;
    const invitations: InvitationsViewModel | undefined = this.invitations();

    if (!invitations) return;

    const payload: string[] = eventValue
      ? invitations.availableToInvite.map((available: InvitePlayerViewModel) => available.userId)
      : [];

    this.selectedUsers.set(payload);
  }

  sendInvitations(): void {
    const users: string[] = this.selectedUsers();

    this.invitePlayersCommandHandler
      .sendInvitations({
        userIdsToInvite: users,
        userIdsToBeMarkedAsFavoriteForNextInvitations: [],
        userIdsToHideForNextInvitations: []
      })
      .pipe(take(1))
      .subscribe(() => this.localRefreshService.emit());
  }
}
